import { BarChart3, TrendingUp, TrendingDown, Trophy, Target, Ban, Gauge, Star, Scale } from "lucide-react";

const Donut = ({ pct, color }) => {
  const r = 42, c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, pct)) / 100) * c;
  return (
    <svg viewBox="0 0 100 100" className="w-28 h-28 -rotate-90">
      <circle cx="50" cy="50" r={r} fill="none" stroke="var(--bg-elevated)" strokeWidth="9" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
    </svg>
  );
};

const Rate = ({ label, value, color, icon: Icon }) => (
  <div>
    <div className="flex justify-between items-center text-xs mb-1">
      <span className="flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}><Icon className="w-3.5 h-3.5" style={{ color }} /> {label}</span>
      <span className="font-mono-term font-semibold">%{value}</span>
    </div>
    <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-elevated)" }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);

const Metric = ({ label, value, color, icon: Icon }) => (
  <div className="rounded-xl border px-3 py-3 text-center" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
    <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
      {Icon && <Icon className="w-3 h-3" style={{ color }} />} {label}
    </div>
    <div className="font-mono-term text-xl font-bold" style={{ color: color || "var(--text-primary)" }}>{value}</div>
  </div>
);

export default function PerformancePanel({ stats }) {
  if (!stats) return null;
  const isUser = stats.scope === "user";
  return (
    <div data-testid="performance-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border-subtle)" }}>
        <BarChart3 className="w-5 h-5 text-blue-500" />
        <h2 className="text-lg font-bold tracking-tight">{isUser ? "Hesap Performansım" : "Oto Sinyal Performansı"}</h2>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* win-rate donut */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative">
            <Donut pct={stats.win_rate} color="var(--long)" />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span data-testid="stat-win-rate" className="font-mono-term text-2xl font-bold" style={{ color: "var(--long)" }}>%{stats.win_rate}</span>
              <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{isUser ? "Başarı" : "Kazanç"}</span>
            </div>
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>{stats.completed} tamamlanan işlem</div>
        </div>

        {/* rates */}
        <div className="space-y-3 justify-center flex flex-col">
          {isUser ? (
            <>
              <Rate label="Kazanan İşlem Oranı" value={stats.tp1_rate} color="var(--long)" icon={Target} />
              <Rate label="Kaybeden İşlem Oranı" value={stats.sl_rate} color="var(--short)" icon={Ban} />
            </>
          ) : (
            <>
              <Rate label="TP1 Oranı" value={stats.tp1_rate} color="var(--long)" icon={Target} />
              <Rate label="TP2 Oranı" value={stats.tp2_rate} color="var(--long)" icon={Target} />
              <Rate label="TP3 Oranı" value={stats.tp3_rate} color="var(--rr)" icon={Trophy} />
              <Rate label="SL Oranı" value={stats.sl_rate} color="var(--short)" icon={Ban} />
            </>
          )}
        </div>

        {/* metrics */}
        <div className="grid grid-cols-2 gap-3 content-center">
          <Metric label="Toplam" value={stats.total} />
          <Metric label="LONG / SHORT" value={`${stats.long_count}/${stats.short_count}`} />
          {isUser ? (
            <>
              <Metric label="Tamamlanan" value={stats.completed} color="var(--rr)" icon={Trophy} />
              <Metric label="Gerçekleşen K/Z" value={`${stats.realized_pnl > 0 ? "+" : ""}${stats.realized_pnl} USDT`} color={stats.realized_pnl >= 0 ? "var(--long)" : "var(--short)"} icon={Scale} />
            </>
          ) : (
            <>
              <Metric label="Ort. Güven" value={`%${stats.avg_confidence}`} color="var(--conf)" icon={Gauge} />
              <Metric label="Ort. Kalite" value={stats.avg_quality} color="var(--rr)" icon={Star} />
              <Metric label="Ort. R/R" value={stats.avg_rr} color="var(--rr)" icon={Scale} />
            </>
          )}
          <Metric label="Açık" value={stats.total - stats.completed} color="var(--entry)" />
        </div>
      </div>
    </div>
  );
}
